import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import content from '../../data/content.json';

const { seo } = content;
const SITE_URL = seo.site_url.replace(/\/$/, '');

/**
 * Seo — per-page <head> tags via react-helmet-async.
 *
 * Copy is sourced from content.json `seo.pages[pageKey]`, with optional
 * explicit overrides. Emits title, meta description, canonical, Open Graph,
 * and Twitter card tags. Pass `jsonLd` (an object) to inject a
 * <script type="application/ld+json"> block (e.g. FAQPage schema).
 *
 * NOTE: these tags are injected by JS on the client. JS-capable crawlers
 * (Googlebot) pick them up on render; the static baseline in public/index.html
 * covers non-JS crawlers until the site is prerendered/SSG'd.
 *
 * @param {string} [pageKey]     Key into seo.pages (e.g. "home", "story")
 * @param {string} [title]       Explicit title override
 * @param {string} [description] Explicit description override
 * @param {object} [jsonLd]      Optional JSON-LD object to embed
 */
const Seo = ({ pageKey, title, description, jsonLd }) => {
  const { pathname } = useLocation();

  const page = (pageKey && seo.pages[pageKey]) || {};
  const finalTitle = title || page.title || seo.default_title;
  const finalDescription = description || page.description || seo.default_description;

  const path = pathname === '/' ? '' : pathname.replace(/\/$/, '');
  const canonical = `${SITE_URL}${path}`;
  const ogImage = `${SITE_URL}${seo.og_image}`;

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDescription} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={content.global.brand} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDescription} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

export default Seo;
